import { alpha, type Theme } from "@mui/material/styles";
import { Info } from "luxon";
import { ALPHA, GRID_RADIUS, SHADOW } from "../../../../../theme/tokens";
import { DAYS_PER_WEEK, NurseActionsErrorTypes } from "../../../../../utils/models";

// Sunday-first index + 6, mod 7, lands on Luxon's Monday-first index.
const DAYS_TO_MONDAY_FIRST = 6;

/** A raised panel grouping one summary section (heading + figures + grid). */
export const sectionSx = (theme: Theme) => ({
	display: "flex",
	flexDirection: "column",
	gap: 1,
	padding: 1.5,
	border: `1px solid ${theme.palette.divider}`,
	borderRadius: GRID_RADIUS.PANEL,
	backgroundColor: theme.palette.background.paper,
	boxShadow: SHADOW.PANEL_SOFT,
});

/** Warning-tinted variant for current-state checks, so they never read as part of the date window. */
export const configurationSectionSx = (theme: Theme) => ({
	...sectionSx(theme),
	borderColor: alpha(theme.palette.warning.main, ALPHA.FOCUS_RING),
	backgroundColor: alpha(theme.palette.warning.main, ALPHA.HINT_TINT),
});

export const gridSx = { width: "100%" } as const;

/**
 * Longest shifts-summary range, in days. Mirrors the backend's `MAX_SHIFTS_SUMMARY_DAYS`: a
 * `UIVisibleError` carries only a key, so the number its message quotes has to come from here.
 * Change both together.
 */
export const MAX_SHIFTS_SUMMARY_DAYS = 92;

/** Interpolation values any summary error message may reference (only `range.too.long` does today). */
export const SUMMARY_ERROR_PARAMS = { maxDays: MAX_SHIFTS_SUMMARY_DAYS } as const;

/**
 * The `UIVisibleError` i18n key a summary request failed with, when the backend sent one. Cast rather
 * than a type guard: react-query types errors as `Error`, and the shape here is the FastAPI error
 * envelope the axios interceptor passes through. Every hop is optional-chained, so any other shape
 * yields `undefined` instead of throwing. Other error types carry prose, not a key, so they're ignored.
 */
export const getSummaryErrorKey = (error: unknown): string | undefined => {
	const detail = (error as { response?: { data?: { detail?: { message?: string; error_type?: string } } } } | null)?.response?.data?.detail;
	return detail?.error_type === NurseActionsErrorTypes.UIVisibleError ? detail.message : undefined;
};

/** Shown in place of a value the backend has no answer for (no job, no pay type, a ratio over zero shifts). */
export const EMPTY_VALUE = "—";

/** `null` is a real answer here (the nurse has no job, the shift has no unit), shown as a dash. */
export const displayNullable = (value: string | null): string => (value === null ? EMPTY_VALUE : value);

/** A 0..1 ratio as a locale percentage; `null` means there was nothing to divide by, not 0%. */
export const formatRatio = (ratio: number | null, language: string): string =>
	ratio === null ? EMPTY_VALUE : new Intl.NumberFormat(language, { style: "percent", maximumFractionDigits: 1 }).format(ratio);

export const formatHours = (hours: number, language: string): string =>
	new Intl.NumberFormat(language, { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(hours);

/**
 * Locale weekday name for a Sun=0 … Sat=6 index. `Info.weekdays` is Monday-first, hence the shift.
 * Read at call time so it follows the active language (i18n updates Luxon's default locale).
 */
export const weekdayNameFromSundayIndex = (sundayIndex: number): string => Info.weekdays("long")[(sundayIndex + DAYS_TO_MONDAY_FIRST) % DAYS_PER_WEEK];
